import type { RescueCase, RescueScenarioType, RescuedOtterCard } from '../types';

export interface RescueScenarioTemplate {
  type: RescueScenarioType;
  title: string;
  description: string;
  stageNames: string[];
  miniGames: ('oil-cleaning' | 'feeding' | 'swimming-lessons' | 'net-removal' | 'habitat-matching' | 'release-prep' | null)[];
  possibleSpecies: string[];
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
}

export const rescueScenarios: RescueScenarioTemplate[] = [
  {
    type: 'oil-spill',
    title: 'Oil Spill Rescue',
    description: 'An otter was found coated in oil after a spill. They need careful cleaning and rehabilitation.',
    stageNames: [
      'Intake Assessment',
      'Oil Cleaning',
      'Warming Station',
      'Feeding & Recovery',
      'Health Check',
      'Release Preparation',
    ],
    miniGames: [null, 'oil-cleaning', null, 'feeding', null, 'release-prep'],
    possibleSpecies: ['sea-otter', 'marine-otter', 'eurasian-otter'],
    rarity: 'rare',
  },
  {
    type: 'orphaned-pup',
    title: 'Orphaned Pup Care',
    description: 'A baby otter was found alone - they need round-the-clock care to survive.',
    stageNames: [
      'Species Identification',
      'Initial Feeding',
      'Swimming Lessons',
      'Hunting Practice',
      'Social Introduction',
      'Wild Skills Test',
      'Release Day',
    ],
    miniGames: ['habitat-matching', 'feeding', 'swimming-lessons', null, null, null, 'release-prep'],
    possibleSpecies: ['sea-otter', 'giant-otter', 'asian-small-clawed-otter', 'north-american-river-otter', 'eurasian-otter'],
    rarity: 'common',
  },
  {
    type: 'net-entanglement',
    title: 'Fishing Net Rescue',
    description: 'An otter got tangled in discarded fishing gear and needs careful rescue.',
    stageNames: [
      'Careful Net Removal',
      'Wound Assessment',
      'Treatment',
      'Physical Therapy',
      'Recovery',
      'Release',
    ],
    miniGames: ['net-removal', null, null, 'swimming-lessons', 'feeding', 'release-prep'],
    possibleSpecies: ['sea-otter', 'eurasian-otter', 'smooth-coated-otter', 'spotted-necked-otter'],
    rarity: 'uncommon',
  },
  {
    type: 'pollution-sickness',
    title: 'Pollution Recovery',
    description: 'This otter got sick from contaminated water and needs medical care.',
    stageNames: [
      'Diagnosis',
      'Treatment Plan',
      'Clean Habitat Setup',
      'Nutrition Management',
      'Recovery Monitoring',
      'Safe Release',
    ],
    miniGames: ['habitat-matching', null, null, 'feeding', null, 'release-prep'],
    possibleSpecies: ['giant-otter', 'smooth-coated-otter', 'neotropical-otter', 'african-clawless-otter'],
    rarity: 'uncommon',
  },
  {
    type: 'habitat-loss',
    title: 'Habitat Loss Refugee',
    description: 'Construction destroyed this otter\'s home - help find them a new safe place.',
    stageNames: [
      'Stress Reduction',
      'Health Assessment',
      'Temporary Housing',
      'New Habitat Scouting',
      'Relocation Prep',
      'New Home Release',
    ],
    miniGames: [null, 'feeding', null, 'habitat-matching', null, 'release-prep'],
    possibleSpecies: ['hairy-nosed-otter', 'smooth-coated-otter', 'asian-small-clawed-otter', 'southern-river-otter'],
    rarity: 'epic',
  },
];

export const otterNames = [
  'Splash', 'Bubble', 'Pebble', 'River', 'Kelp', 'Coral', 'Finn', 'Wave',
  'Shimmer', 'Dusk', 'Dawn', 'Misty', 'Luna', 'Sol', 'Storm', 'Breeze',
  'Sandy', 'Rocky', 'Muddy', 'Willow', 'Reed', 'Lotus', 'Clover', 'Ivy',
  'Jasper', 'Opal', 'Pearl', 'Jade', 'Amber', 'Slate', 'Flint', 'Brook',
  'Otter Pop', 'Noodle', 'Whiskers', 'Squeaky', 'Waddle', 'Snoot', 'Boop',
];

export function generateOtterName(): string {
  return otterNames[Math.floor(Math.random() * otterNames.length)];
}

export function generateRescueCase(scenarioType?: RescueScenarioType): RescueCase {
  const template = scenarioType
    ? rescueScenarios.find(s => s.type === scenarioType)!
    : rescueScenarios[Math.floor(Math.random() * rescueScenarios.length)];

  const speciesId = template.possibleSpecies[
    Math.floor(Math.random() * template.possibleSpecies.length)
  ];

  return {
    id: `case-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    scenarioType: template.type,
    otterName: generateOtterName(),
    speciesId,
    story: template.description,
    stages: template.stageNames.map((name, index) => ({
      id: `stage-${index}`,
      name,
      description: getStageDescription(template.type, index),
      miniGame: template.miniGames[index] || undefined,
      completed: false,
    })),
    completed: false,
    currentStage: 0,
  };
}

function getStageDescription(type: RescueScenarioType, stageIndex: number): string {
  const descriptions: Record<RescueScenarioType, string[]> = {
    'oil-spill': [
      'Check the otter\'s condition and identify any injuries.',
      'Carefully clean the oil from their fur without causing stress.',
      'Keep the otter warm while their fur recovers its insulation.',
      'Provide nutritious food to help them regain strength.',
      'Make sure they\'re healthy enough for release.',
      'Prepare for a successful return to the wild!',
    ],
    'orphaned-pup': [
      'Determine what species this little one is.',
      'Start bottle feeding on a careful schedule.',
      'Teach this pup how to swim like a pro!',
      'Practice catching fish and foraging.',
      'Meet other otters to learn social skills.',
      'Test their survival skills.',
      'Time to go home!',
    ],
    'net-entanglement': [
      'Carefully untangle the fishing net without hurting the otter.',
      'Check for any cuts or injuries from the net.',
      'Treat any wounds with care.',
      'Help them regain swimming strength.',
      'Make sure they\'re eating well.',
      'Back to freedom!',
    ],
    'pollution-sickness': [
      'Figure out what made this otter sick.',
      'Create a treatment plan.',
      'Set up a clean, toxin-free environment.',
      'Special diet to help them recover.',
      'Watch their progress carefully.',
      'Find a clean habitat for release.',
    ],
    'habitat-loss': [
      'Help the otter calm down after losing their home.',
      'Make sure they\'re physically healthy.',
      'Create a comfortable temporary space.',
      'Find the perfect new home location.',
      'Get everything ready for the move.',
      'Welcome to your new home!',
    ],
  };

  return descriptions[type][stageIndex] || 'Complete this stage to continue.';
}

export function generateOtterCard(
  rescueCase: RescueCase,
  recoveryDays: number
): RescuedOtterCard {
  const template = rescueScenarios.find(s => s.type === rescueCase.scenarioType)!;

  return {
    id: `card-${rescueCase.id}`,
    name: rescueCase.otterName,
    speciesId: rescueCase.speciesId,
    scenarioType: rescueCase.scenarioType,
    rescuedDate: new Date().toISOString(),
    recoveryDays,
    rarity: template.rarity,
  };
}

export const rescueFacts: Record<RescueScenarioType, string[]> = {
  'oil-spill': [
    'Oil destroys the waterproofing of otter fur. Without it, they can die from hypothermia in just hours.',
    'It can take rescuers 45 minutes or more to properly clean an oiled otter.',
    'Otters must be kept calm during cleaning - stress can be as dangerous as the oil!',
    'The Exxon Valdez spill in 1989 killed an estimated 2,800 sea otters.',
    'Rehabilitated otters are usually kept for several weeks before release to ensure full recovery.',
  ],
  'orphaned-pup': [
    'Baby sea otters can\'t swim or dive when they\'re born - mom has to teach them!',
    'Otter pups are nursed for several months before learning to find food on their own.',
    'Orphaned pups need to be fed every few hours, even through the night.',
    'Surrogate otter moms are sometimes used to help raise orphaned pups!',
    'Giant otter pups start swimming lessons at about 2 months old.',
  ],
  'net-entanglement': [
    'Discarded fishing nets, called "ghost nets," kill millions of marine animals each year.',
    'Entangled otters can drown if they can\'t reach the surface to breathe.',
    'Many injuries from nets can be prevented by properly disposing of fishing gear.',
    'Some organizations organize beach cleanups specifically to remove dangerous nets.',
    'Biodegradable fishing nets are being developed to reduce this problem.',
  ],
  'pollution-sickness': [
    'Otters are indicator species - when they get sick, it often means the water is polluted.',
    'Toxins build up in otter bodies because they eat so much contaminated fish.',
    'Clean water regulations have helped many otter populations recover from pollution.',
    'Otters help keep ecosystems healthy, which is why saving them matters so much!',
    'Scientists study otter health to monitor the overall health of rivers and coasts.',
  ],
  'habitat-loss': [
    'More than half of the world\'s wetlands have been destroyed in the past century.',
    'Otters need large territories with clean water to survive.',
    'Some otters have learned to adapt to urban environments, but many species can\'t.',
    'Protecting otter habitats also protects hundreds of other species!',
    'Creating wildlife corridors helps otters move between fragmented habitats.',
  ],
};

export function getRandomRescueFact(type: RescueScenarioType): string {
  const facts = rescueFacts[type];
  return facts[Math.floor(Math.random() * facts.length)];
}

export const facilityDescriptions = {
  medicalBay: {
    name: 'Medical Bay',
    description: 'Better equipment for faster healing',
    levels: [
      'Basic first aid supplies',
      'X-ray machine and lab tests',
      'Full surgical suite',
      'World-class veterinary care',
    ],
    benefits: ['Normal healing', '+25% faster healing', '+50% faster healing', '+100% faster healing'],
  },
  nursery: {
    name: 'Nursery',
    description: 'Care for more pups at once',
    levels: [
      'Single pup capacity',
      'Room for 2 pups',
      'Room for 3 pups',
      'Full nursery - 5 pups!',
    ],
    benefits: ['1 pup max', '2 pups max', '3 pups max', '5 pups max'],
  },
  trainingPool: {
    name: 'Training Pool',
    description: 'Better preparation for release',
    levels: [
      'Small practice pool',
      'Natural-style pool',
      'Large habitat pool',
      'Full ecosystem simulation!',
    ],
    benefits: ['Basic training', '+25% success rate', '+50% success rate', '+100% success rate'],
  },
  researchLab: {
    name: 'Research Lab',
    description: 'Unlock new treatments',
    levels: [
      'Basic research notes',
      'Lab equipment',
      'Advanced diagnostics',
      'Cutting-edge otter science!',
    ],
    benefits: ['Standard treatments', 'New treatments unlocked', 'Rare treatments', 'Experimental cures'],
  },
};

export const rankInfo = {
  'volunteer': { name: 'Volunteer', minRescues: 0, badge: '🌱', description: 'Just starting your rescue journey!' },
  'junior-keeper': { name: 'Junior Keeper', minRescues: 6, badge: '🦦', description: 'You\'re getting the hang of this!' },
  'senior-keeper': { name: 'Senior Keeper', minRescues: 16, badge: '🏅', description: 'A skilled otter rescuer!' },
  'rehabilitation-expert': { name: 'Rehab Expert', minRescues: 31, badge: '⭐', description: 'Otters are lucky to have you!' },
  'conservation-hero': { name: 'Conservation Hero', minRescues: 51, badge: '🏆', description: 'A true otter champion!' },
};
